import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { apiClient } from '@/lib/apiClient';
import { authService } from '@/services/auth/authService';
import { CACHE_TIME } from '@/config/queryDefaults';

export interface EffectiveFieldRestriction {
  fieldName: string;
  restrictionType: 'HIDDEN' | 'READ_ONLY';
}

interface EffectiveFieldRestrictionsResponse {
  moduleName: string;
  entityName: string;
  restrictions: EffectiveFieldRestriction[];
}

/**
 * Query key factory for field restrictions.
 * Includes tenantId so different tenants have separate caches.
 */
export const fieldRestrictionKeys = {
  all: (tenantId?: string | null) => ['fieldRestrictions', tenantId] as const,
  effective: (tenantId?: string | null, moduleName?: string, entityName?: string) =>
    [...fieldRestrictionKeys.all(tenantId), 'effective', moduleName ?? '', entityName ?? ''] as const,
};

/**
 * Hook to fetch effective field restrictions for the current user on a specific entity.
 * The backend merges restrictions across all the user's roles (least restrictive wins).
 *
 * Returns a Map<fieldName, 'HIDDEN' | 'READ_ONLY'> for easy lookup.
 * Fields not in the map have no restriction (full access).
 */
export function useFieldRestrictions(moduleName: string, entityName: string) {
  const tenantId = authService.getTenantId();

  const { data, isLoading } = useQuery<EffectiveFieldRestrictionsResponse>({
    queryKey: fieldRestrictionKeys.effective(tenantId, moduleName, entityName),
    queryFn: async () => {
      const response = await apiClient.get<EffectiveFieldRestrictionsResponse>(
        '/api/core/fieldrestrictionset/effective',
        { params: { moduleName, entityName } }
      );
      return response.data;
    },
    staleTime: CACHE_TIME.METADATA,
    enabled: !!moduleName && !!entityName,
    retry: false, // Don't retry on 403 - degrade gracefully
  });

  const restrictions = useMemo(() => {
    const map = new Map<string, 'HIDDEN' | 'READ_ONLY'>();
    if (data?.restrictions) {
      for (const r of data.restrictions) {
        map.set(r.fieldName, r.restrictionType);
      }
    }
    return map;
  }, [data]);

  return { restrictions, isLoading };
}
